/* /expenses — expense claims: list + filters, approve/reject, new claim with receipt upload and AI receipt capture. */
import "./pay.css";
import { useEffect, useMemo, useState } from "react";
import { useData } from "../../data";
import { useAiExtract, useCreateExpense, useDecideExpense, useExpenses, useToday, useUploadFile } from "../../data/hooks";
import type { Expense, ExpenseInput } from "../../data/types";
import { useI18n } from "../../i18n";
import { fmtN, gDate } from "../../lib/format";
import {
  Affix, AiNote, Button, EmptyState, ErrorBox, Field, Input, Kpi, Modal, Money, PageHeader, Person, Seg, SelectField, Skeleton, TextField, errMsg, useToast,
} from "../../ui";
import { StatusChip, numOf, unknownEmp, useEmpMap, useRole } from "./components/common";
import { curL } from "../../lib/country";

export const EXPENSE_CATEGORIES = ["Office supplies", "Business travel", "Transport", "Client entertainment", "Meals", "Training", "Other"];
const CAT_AR: Record<string, string> = { "Office supplies": "مستلزمات مكتبية", "Business travel": "سفر عمل", Transport: "مواصلات", "Client entertainment": "ضيافة العملاء", Meals: "وجبات", Training: "تدريب", Other: "أخرى" };

type F = { employeeId: string; vendor: string; category: string; date: string; amount: string; vat: string };

export function validateExpense(f: F, today: string): Record<string, string> {
  const e: Record<string, string> = {};
  if (!f.employeeId) e.employeeId = "required";
  if (!f.vendor.trim()) e.vendor = "required";
  if (!f.category.trim()) e.category = "required";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.date)) e.date = "valid date required";
  else if (f.date > today) e.date = "cannot be in the future";
  const a = numOf(f.amount), v = numOf(f.vat);
  if (!(a > 0)) e.amount = "must be > 0";
  if (Number.isNaN(v) || v < 0) e.vat = "must be ≥ 0";
  else if (a > 0 && v > a) e.vat = "cannot exceed amount";
  return e;
}

function NewExpenseModal({ onClose, autoAi }: { onClose: () => void; autoAi: boolean }) {
  const { t, L, name } = useI18n();
  const toast = useToast();
  const today = useToday();
  const emps = useEmpMap();
  const upload = useUploadFile();
  const ai = useAiExtract();
  const create = useCreateExpense();
  const [f, setF] = useState<F>({ employeeId: "", vendor: "", category: "", date: today, amount: "", vat: "0" });
  const [receipt, setReceipt] = useState<{ key: string; name: string } | null>(null);
  const [aiInfo, setAiInfo] = useState<{ conf: number; model: string } | null>(null);
  const [touched, setTouched] = useState(false);
  const [srv, setSrv] = useState<Record<string, string>>({});
  const [ranAuto, setRanAuto] = useState(false);
  const empId = f.employeeId || String(emps.list[0]?.id ?? "");
  const form = { ...f, employeeId: empId };
  const errs = touched ? { ...validateExpense(form, today), ...srv } : srv;
  const set = (k: keyof F) => (v: string) => { setF((x) => ({ ...x, [k]: v })); setSrv((s) => { const n = { ...s }; delete n[k]; return n; }); };

  const readAi = async (fileKey?: string) => {
    try {
      const r = await ai.mutateAsync({ feature: "expense", fileKey });
      const x = r.fields;
      setF((cur) => ({
        ...cur,
        vendor: x.vendor != null ? String(x.vendor) : cur.vendor,
        category: x.category != null ? String(x.category) : cur.category,
        date: typeof x.date === "string" ? x.date : cur.date,
        amount: x.amount != null ? String(x.amount) : cur.amount,
        vat: x.vat != null ? String(x.vat) : cur.vat,
      }));
      setAiInfo({ conf: r.confidence, model: r.model });
      toast(L("Receipt read by AI · check the fields", "قرأ الذكاء الفاتورة · راجع الحقول"));
    } catch (e) { toast(errMsg(e), "bad"); }
  };
  useEffect(() => {
    if (autoAi && !ranAuto) { setRanAuto(true); void readAi(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoAi, ranAuto]);

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    try {
      const ref = await upload.mutateAsync({ file, name: file.name, folder: "expenses" });
      setReceipt({ key: ref.key, name: ref.name });
      toast(L("Receipt uploaded", "تم رفع الفاتورة"));
    } catch (e) { toast(errMsg(e), "bad"); }
  };

  const submit = async () => {
    setTouched(true);
    if (Object.keys(validateExpense(form, today)).length) return;
    const input: ExpenseInput = { employeeId: Number(empId), vendor: f.vendor.trim(), category: f.category.trim(), date: f.date, amount: numOf(f.amount), vat: numOf(f.vat), ai: !!aiInfo };
    if (receipt) input.receiptKey = receipt.key;
    try {
      await create.mutateAsync(input);
      toast(L("Expense claim submitted", "تم إرسال المطالبة"));
      onClose();
    } catch (e) {
      const fe = (e as { fields?: Record<string, string> }).fields;
      if (fe && Object.keys(fe).length) setSrv(fe); else toast(errMsg(e), "bad");
    }
  };

  return (
    <Modal title={L("New expense claim", "مطالبة مصروف جديدة")} onClose={onClose} testId="expense-modal"
      foot={<><Button onClick={onClose}>{t("Cancel")}</Button><Button variant="pri" onClick={submit} loading={create.isPending} data-testid="expense-submit">{t("Submit")}</Button></>}>
      <div className="stack">
        <div className="aibar">
          <div className="grow">
            <b>{L("Receipt", "الفاتورة")}</b>
            <span className="xs muted" style={{ display: "block" }}>{L("Upload a photo or PDF, then let AI read vendor, date, amount and VAT.", "ارفع صورة أو PDF ودع الذكاء يقرأ المورد والتاريخ والمبلغ والضريبة.")}</span>
            <div className="pay-filerow mt">
              <label className="btn sm"><input type="file" accept="image/*,application/pdf" style={{ display: "none" }} aria-label={L("Upload receipt", "رفع الفاتورة")} data-testid="expense-file" onChange={(e) => void onFile(e.target.files?.[0])} /><span>{upload.isPending ? L("Uploading…", "جارٍ الرفع…") : L("Upload receipt", "رفع الفاتورة")}</span></label>
              {receipt && <span className="chip ok dot" data-testid="expense-receipt">{receipt.name}</span>}
            </div>
          </div>
          <Button className="aibtn" icon="sparkle" onClick={() => void readAi(receipt?.key)} loading={ai.isPending} data-testid="expense-ai">{L("Read with AI", "قراءة بالذكاء")}</Button>
        </div>
        {aiInfo && <AiNote>{L("Filled by AI (" + aiInfo.model + ", " + Math.round(aiInfo.conf * 100) + "% confidence). Please review before submitting.", "عبّأها الذكاء (" + aiInfo.model + "، ثقة " + Math.round(aiInfo.conf * 100) + "%). راجعها قبل الإرسال.")}</AiNote>}
        <SelectField label={t("Employee")} value={empId} onChange={set("employeeId")} options={emps.list.map((e) => [e.id, name(e) + " · " + e.code])} err={errs.employeeId} testId="expense-employee" req />
        <TextField label={L("Vendor", "المورد")} value={f.vendor} onChange={set("vendor")} err={errs.vendor} testId="expense-vendor" req />
        <div className="form2">
          <SelectField label={L("Category", "الفئة")} value={f.category} onChange={set("category")} options={[["", L("Choose…", "اختر…")], ...EXPENSE_CATEGORIES.map((c): [string, string] => [c, L(c, CAT_AR[c] ?? c)])]} err={errs.category} testId="expense-category" req />
          <TextField label={L("Date", "التاريخ")} type="date" value={f.date} onChange={set("date")} err={errs.date} testId="expense-date" req />
          <Field label={t("Amount")} req err={errs.amount} htmlFor="exp-amt">
            <Affix pre={curL(L)}><Input id="exp-amt" className="num" type="number" min={0} step="0.01" value={f.amount} onChange={(e) => set("amount")(e.target.value)} bad={!!errs.amount} data-testid="expense-amount" /></Affix>
          </Field>
          <Field label={L("VAT included", "الضريبة المشمولة")} err={errs.vat} htmlFor="exp-vat" hint={L("15% VAT = amount × 15/115", "ضريبة 15% = المبلغ × 15/115")}>
            <Affix pre={curL(L)}><Input id="exp-vat" className="num" type="number" min={0} step="0.01" value={f.vat} onChange={(e) => set("vat")(e.target.value)} bad={!!errs.vat} data-testid="expense-vat" /></Affix>
          </Field>
        </div>
      </div>
    </Modal>
  );
}

export default function Expenses() {
  const { t, L, lang } = useI18n();
  const toast = useToast();
  const data = useData();
  const q = useExpenses();
  const emps = useEmpMap();
  const decide = useDecideExpense();
  const { can } = useRole();
  const [filter, setFilter] = useState<"all" | "Pending" | "Approved" | "Rejected" | "Reimbursed">("all");
  const [modal, setModal] = useState<null | "new" | "ai">(null);
  const [busy, setBusy] = useState<number | null>(null);

  const list = useMemo(() => (q.data ?? []).filter((x) => filter === "all" || x.status === filter), [q.data, filter]);
  if (q.error) return <ErrorBox error={q.error} onRetry={() => q.refetch()} />;
  if (!q.data) return <Skeleton />;
  const all = q.data;

  const doDecide = (x: Expense, approve: boolean) => {
    setBusy(x.id);
    decide.mutate({ id: x.id, approve }, {
      onSuccess: () => toast(approve ? L("Expense approved · paid with next payroll", "تم الاعتماد · يُصرف مع الراتب القادم") : L("Expense rejected", "تم رفض المصروف"), approve ? "ok" : "bad"),
      onError: (e) => toast(errMsg(e), "bad"),
      onSettled: () => setBusy(null),
    });
  };

  return (
    <>
      <PageHeader title={L("Expenses", "المصروفات")} sub={L("Employees submit receipts; approved claims are paid with the next payroll.", "يرفع الموظفون الفواتير وتُصرف المطالبات المعتمدة مع الراتب القادم.")}
        actions={<>
          <Button className="aibtn" icon="sparkle" onClick={() => setModal("ai")} data-testid="btn-scan">{L("Scan a receipt", "مسح فاتورة")}</Button>
          <Button variant="pri" icon="plus" onClick={() => setModal("new")} data-testid="btn-new-expense">{L("New claim", "مطالبة جديدة")}</Button>
        </>} />
      <div className="g g-4 mb">
        <Kpi testId="kpi-pending" label={t("Pending")} value={String(all.filter((x) => x.status === "Pending").length)} icon="inbox" tone="warn" />
        <Kpi testId="kpi-topay" label={L("To pay with payroll", "للصرف مع الرواتب")} value={fmtN(all.filter((x) => x.status === "Approved").reduce((a, x) => a + x.amount, 0), 0)} unit={curL(L)} icon="wallet" />
        <Kpi testId="kpi-vat" label={L("Input VAT captured", "ضريبة المدخلات")} value={fmtN(all.reduce((a, x) => a + x.vat, 0), 2)} unit={curL(L)} icon="receipt" tone="info" />
        <Kpi testId="kpi-ai" label={L("Read by AI", "قرأها الذكاء")} value={all.filter((x) => x.ai).length + " / " + all.length} icon="sparkle" tone="gold" />
      </div>
      <section className="card">
        <div className="toolbar">
          <Seg label={t("Status")} value={filter} onChange={setFilter} items={[["all", L("All", "الكل")], ["Pending", t("Pending")], ["Approved", t("Approved")], ["Rejected", t("Rejected")], ["Reimbursed", L("Paid", "مصروف")]]} />
        </div>
        {list.length === 0 ? <EmptyState title={L("No expense claims", "لا توجد مطالبات")} desc={L("New claims will appear here.", "ستظهر المطالبات الجديدة هنا.")} /> : (
          <div className="tw">
            <table className="tbl cards" data-testid="expenses-table">
              <thead><tr><th>{t("Employee")}</th><th>{L("Vendor", "المورد")}</th><th className="hide-md">{L("Category", "الفئة")}</th><th className="end">{t("Amount")}</th><th>{t("Status")}</th><th className="end"><span className="pay-sr">{t("Actions")}</span></th></tr></thead>
              <tbody>
                {list.map((x) => (
                  <tr key={x.id} data-testid={"expense-row-" + x.id}>
                    <td className="k1"><Person e={emps.map.get(x.employeeId) ?? unknownEmp(x.employeeId)} /></td>
                    <td className="k3" data-l={L("Vendor", "المورد")}>
                      {x.vendor}{x.ai && <> <span className="aitag sm">AI</span></>}
                      <div className="xs muted">{gDate(x.date, lang)}{x.receiptKey && <> · <a href={data.fileUrl(x.receiptKey)} target="_blank" rel="noreferrer" data-testid={"receipt-" + x.id}>{L("Receipt", "الفاتورة")}</a></>}</div>
                    </td>
                    <td className="hide-md">{L(x.category, CAT_AR[x.category] ?? x.category)}</td>
                    <td className="end k3" data-l={t("Amount")}><Money value={x.amount} /><div className="xs muted">{L("VAT ", "ضريبة ")}<Money value={x.vat} plain /></div></td>
                    <td className="k2"><StatusChip status={x.status} /></td>
                    <td className="end k3" data-l="">
                      {x.status === "Pending" && can("requests.decide") && (
                        <div className="row nw" style={{ justifyContent: "flex-end" }}>
                          <Button variant="ghost sm" onClick={() => doDecide(x, false)} disabled={busy === x.id} data-testid={"btn-reject-" + x.id}>{t("Reject")}</Button>
                          <Button variant="sm" onClick={() => doDecide(x, true)} loading={busy === x.id} data-testid={"btn-approve-" + x.id}>{t("Approve")}</Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {modal && <NewExpenseModal onClose={() => setModal(null)} autoAi={modal === "ai"} />}
    </>
  );
}
